import { useId, type ReactNode } from "react";
import { formatEur, formatKc, type BudgetCardItem } from "../../schemas/budget";

/**
 * UC-002 Per-SPZ expandable row inline detail (Phase 16).
 *
 * Renderuje drill-down obsah pro 1 SPZ: per-transakce rozklad s
 * `occurred_at`, `fuel_card_label`, `amount_kc`, `amount_eur` (paralelně,
 * REQ-BIZ-008), volitelný `note` a lokalitu.
 *
 * Visible jen když `expanded === true`. Skrytý fragment se NEvyrenderuje
 * (žádné aria-hidden trickery — když je expanded false, DOM nereferuje
 * sekci, aby axe nedetekoval orphan landmarky).
 *
 * Accessibility:
 *   - `role="region"` + aria-labelledby na header HMM rowu.
 *   - color-not-only — paralelní CZK + EUR + transakční count.
 */

export interface SpzExpandableRowProps {
  item: BudgetCardItem;
  expanded: boolean;
  /** ID aria-labelledby — typicky `<spz-row-${id}-label>`. */
  labelledById: string;
  /** Hook pro UnassignedSpzSlideOver kontext — neukládá expanded state. */
  rightActions?: ReactNode;
}

export function SpzExpandableRow({
  item,
  expanded,
  labelledById,
  rightActions,
}: SpzExpandableRowProps) {
  const regionId = useId();
  if (!expanded) return null;
  const th =
    "text-left px-4 py-3 border-b border-secondary font-semibold bg-secondary text-tertiary text-xs uppercase tracking-wider";
  const td = "text-left px-4 py-3 border-b border-secondary";
  return (
    <section
      id={regionId}
      role="region"
      aria-labelledby={labelledById}
      className="flex flex-col gap-3 px-6 py-4"
      data-testid={`spz-expand-${item.fuel_card_id}`}
    >
      <header className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-md font-bold m-0">
          {item.card_label ?? item.card_number_display ?? "—"}
          {item.holder_label ? ` · ${item.holder_label}` : ""}
        </h4>
        <div className="inline-flex gap-2 text-sm text-primary">
          <span data-testid="spz-expand-amount-kc">{formatKc(item.amount_kc)}</span>
          <span aria-hidden="true">·</span>
          <span data-testid="spz-expand-amount-eur">{formatEur(item.amount_eur)}</span>
          <span aria-hidden="true">·</span>
          <span>{item.transaction_count} transakcí</span>
        </div>
        {rightActions && <div className="flex gap-2">{rightActions}</div>}
      </header>
      <table
        className="w-full border-collapse text-sm text-primary"
        aria-label={`Transakce ${item.card_label ?? item.card_number_display ?? ""}`}
      >
        <thead>
          <tr>
            <th scope="col" className={th}>Datum</th>
            <th scope="col" className={th}>SPZ</th>
            <th scope="col" className={th}>Lokalita</th>
            <th scope="col" className={th}>CZK</th>
            <th scope="col" className={th}>EUR</th>
            <th scope="col" className={th}>Litry</th>
            <th scope="col" className={th}>Poznámka</th>
          </tr>
        </thead>
        <tbody>
          {item.transactions.length === 0 ? (
            <tr>
              <td colSpan={7} className="text-center text-tertiary py-8">
                Žádné transakce.
              </td>
            </tr>
          ) : (
            item.transactions.map((tx) => (
              <tr
                key={tx.fuel_transaction_id}
                className="hover:bg-secondary"
                data-testid={`spz-expand-tx-${tx.fuel_transaction_id}`}
              >
                <td className={td}>
                  <time dateTime={tx.occurred_at}>{formatDate(tx.occurred_at)}</time>
                </td>
                <td className={td}>{tx.spz ?? "—"}</td>
                <td className={td}>{tx.location ?? "—"}</td>
                <td className={td}>{formatKc(tx.amount_kc)}</td>
                <td className={td}>{tx.amount_eur ? formatEur(tx.amount_eur) : "—"}</td>
                <td className={td}>{tx.liters ?? "—"}</td>
                <td className={td}>{tx.note ?? "—"}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </section>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("cs-CZ", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
